import React, { useEffect, useState } from 'react';
import { collection, onSnapshot, query, orderBy, getDocs, doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import EventEditor from './EventEditor';
import ConfirmationDialog from '../components/ConfirmationDialog';
import WelcomeDialog from '../components/WelcomeDialog';
import { preloadImages, preloadImagesAsync } from '../utils/preloadImages';
import { updateLastModified } from '../utils/backup';
import { fetchIncomingShares, fetchSharedItems } from '../utils/sharing';
import { CarTemplates } from '../components/VehicleEditor/DefaultParameters';
import '../styles.css';

function Main({ setSelectedVehicle, setSelectedEvent, subscriptionStatus }) {
  const [vehicles, setVehicles] = useState([]);
  const [events, setEvents] = useState([]);
  const [sharedVehicles, setSharedVehicles] = useState([]);
  const [sharedEvents, setSharedEvents] = useState([]);
  const [vehiclesLoaded, setVehiclesLoaded] = useState(false);
  const [eventsLoaded, setEventsLoaded] = useState(false);
  const [sharedLoaded, setSharedLoaded] = useState(false);
  const [pageReady, setPageReady] = useState(false);
  const [syncStatus, setSyncStatus] = useState('syncing');
  const [selectedVehicle, setLocalSelectedVehicle] = useState(null);
  const [showEventWarning, setShowEventWarning] = useState(false);
  const [showEventEditor, setShowEventEditor] = useState(false);
  const [showMoreEvents, setShowMoreEvents] = useState(false);
  // Empty set means "all vehicles" (no filter applied).
  const [selectedVehicleKeys, setSelectedVehicleKeys] = useState(() => new Set());
  const [showMoreVehicles, setShowMoreVehicles] = useState(false);
  const [showVehicleLimitDialog, setShowVehicleLimitDialog] = useState(false);
  const [showTireManagerDialog, setShowTireManagerDialog] = useState(false);
  const [showWelcomeDialog, setShowWelcomeDialog] = useState(false);
  const [hideGettingStarted, setHideGettingStarted] = useState(true);
  const [gettingStartedLoaded, setGettingStartedLoaded] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [showHideDialog, setShowHideDialog] = useState(false);
  const [user, loading, error] = useAuthState(auth);
  const navigate = useNavigate();

  // Getting Started Videos - PASTE YOUR YOUTUBE URLs HERE
  const gettingStartedVideos = [
    { title: 'Getting Started With Hot Setup', url: 'https://youtu.be/JNY0JJ77XE0' },
    { title: 'Vehicle Editor', url: 'https://youtu.be/jZ31GDsbyR4' },
    { title: 'Creating Sessions', url: 'https://youtu.be/1NzFc09_hgU' },
    { title: 'Circuit Manager', url: 'https://youtu.be/r4-avCO32ls' },
    { title: 'Comparing Sessions', url: 'https://youtu.be/1mUimHq84bA' },
    { title: 'Tire Manager', url: 'https://youtu.be/h3DIC0dzxek' },
    { title: 'How To Install Hot Setup', url: 'https://youtu.be/YuxDC7RVx6E' },
    { title: 'Data Backup / Restore', url: 'https://youtu.be/9MDvFmelQjA' },
    { title: 'Data Encryption', url: 'https://youtu.be/xhn0niJq6pE' },    
  ];

  const getYouTubeVideoId = (url) => {
    let videoId = null;
    if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1].split(/[?#]/)[0];
    } else {
      const match = url.match(/[?&]v=([^#\&\?]*).*/);
      videoId = match && match[1];
    }
    return videoId;
  };

  const currentVideo = gettingStartedVideos[currentVideoIndex];
  const videoId = getYouTubeVideoId(currentVideo.url);
  const embedUrl = videoId ? `https://www.youtube.com/embed/${videoId}` : '';

  // Resolve all vehicle names for an event (multi-vehicle aware), joined for display.
  const getVehicleNames = (event) => {
    const ids = event?.vehicleIds || (event?.vehicleId !== undefined && event?.vehicleId !== null && event?.vehicleId !== '' ? [event.vehicleId] : []);
    if (ids.length === 0) return '';
    const ownerUid = event?.ownerUid;
    const pool = [...vehicles, ...sharedVehicles];
    const resolve = (id) => {
      const v = pool.find(veh => String(veh.id) === String(id) && (ownerUid ? veh.ownerUid === ownerUid : !veh.ownerUid))
        || pool.find(veh => String(veh.id) === String(id));
      return v ? (v.name || 'Unnamed Vehicle') : null;
    };
    return ids.map(resolve).filter(Boolean).join(', ');
  };

  const formatEventDate = (startDate, endDate) => {
    if (!startDate) return '';
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : start;
    const startMonth = months[start.getMonth()];
    const endMonth = months[end.getMonth()];
    const startDay = start.getDate();
    const endDay = end.getDate();
    const year = start.getFullYear();
    if (startMonth === endMonth) {
      if (startDay === endDay) {
        return `${startMonth} ${startDay}, ${year}`;
      } else {
        return `${startMonth} ${startDay}-${endDay}, ${year}`;
      }
    } else {
      return `${startMonth} ${startDay}-${endMonth} ${endDay}, ${year}`;
    }
  };

  const handleAddVehicle = () => {
    if (subscriptionStatus?.plan === 'enthusiast' && vehicles.length >= 3) {
      setShowVehicleLimitDialog(true);
    } else {
      addVehicle();
    }
  };

  const handleTireManagerClick = () => {
    if (subscriptionStatus?.plan === 'enthusiast') {
      setShowTireManagerDialog(true);
    } else {
      navigate('/tire-manager');
    }
  };

  const handleUpgrade = () => {
    navigate('/pricing');
  };

  const handleHideGettingStarted = async () => {
    setShowHideDialog(false);
    if (user) {
      const userDocRef = doc(db, `users/${user.uid}`);
      await updateDoc(userDocRef, { HideGettingStarted: true });
      setHideGettingStarted(true);
    }
  };

  useEffect(() => {
    if (loading) {
      setSyncStatus('syncing');
      return;
    }
    if (error || !user) {
      console.error('Auth error or no user:', error);
      setSyncStatus('error');
      navigate('/login');
      return;
    }

    // Preload all collections to populate Firestore cache
    const preloadCollections = async () => {
      const collections = ['vehicles', 'events', 'sessions', 'circuits'];
      try {
        for (const colName of collections) {
          const q = query(collection(db, `users/${user.uid}/${colName}`), orderBy('timestamp', 'desc'));
          await getDocs(q); // Fetch to cache
        }
      } catch (err) {
        console.error('Preload error:', err.message);
        setSyncStatus('error');
      }
    };

    // Preload images from Firebase Storage
    const preloadAllImages = async () => {
      try {
        const vehicleQuery = query(collection(db, `users/${user.uid}/vehicles`));
        const vehicleSnap = await getDocs(vehicleQuery);
        const imageUrls = vehicleSnap.docs
          .map(doc => doc.data().image)
          .filter(url => url && url.includes('firebasestorage.googleapis.com'));
        const circuitQuery = query(collection(db, `users/${user.uid}/circuits`));
        const circuitSnap = await getDocs(circuitQuery);
        imageUrls.push(...circuitSnap.docs
          .map(doc => [doc.data().image, doc.data().trackMap])
          .flat()
          .filter(url => url && url.includes('firebasestorage.googleapis.com')));
        preloadImages(imageUrls);
      } catch (err) {
        console.error('Image preload error:', err.message);
      }
    };

    // Real-time listener for vehicles
    const vehiclesQuery = query(collection(db, `users/${user.uid}/vehicles`), orderBy('timestamp', 'desc'));
    const unsubscribeVehicles = onSnapshot(vehiclesQuery, (snapshot) => {
      const fetchedVehicles = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const vehiclesWithFallback = fetchedVehicles.map(vehicle => ({
        ...vehicle,
        image: vehicle.image || '/images/default-vehicle.jpg',
      }));
      setVehicles(vehiclesWithFallback);
      preloadImages(vehiclesWithFallback.map(v => v.image).filter(Boolean));
      setVehiclesLoaded(true);
      setSyncStatus(navigator.onLine ? 'synced' : 'offline');
    }, (err) => {
      console.error('Vehicles fetch error:', err);
      setVehiclesLoaded(true);
      setSyncStatus('error');
    });

    // Real-time listener for events
    const eventsQuery = query(collection(db, `users/${user.uid}/events`), orderBy('startDate', 'desc'));
    const unsubscribeEvents = onSnapshot(eventsQuery, (snapshot) => {
      const fetchedEvents = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const eventsWithFallback = fetchedEvents.map(event => ({
        ...event,
        image: event.image || '/images/default-event.jpg',
      }));
      setEvents(eventsWithFallback);
      preloadImages(eventsWithFallback.map(e => e.image).filter(Boolean));
      setEventsLoaded(true);
      setSyncStatus(navigator.onLine ? 'synced' : 'offline');
    }, (err) => {
      console.error('Events fetch error:', err);
      setEventsLoaded(true);
      setSyncStatus('error');
    });

    // Run preload
    preloadCollections();
    preloadAllImages();

    // Update sync status on connectivity
    const updateSyncStatus = () => {
      setSyncStatus(navigator.onLine ? 'synced' : 'offline');
    };
    window.addEventListener('online', updateSyncStatus);
    window.addEventListener('offline', updateSyncStatus);

    return () => {
      unsubscribeVehicles();
      unsubscribeEvents();
      window.removeEventListener('online', updateSyncStatus);
      window.removeEventListener('offline', updateSyncStatus);
    };
  }, [user, loading, error, navigate]);

  // Check if user needs welcome dialog and fetch hide getting started
  useEffect(() => {
    if (user) {
      const checkInitialization = async () => {
        const userDocRef = doc(db, `users/${user.uid}`);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const data = userDoc.data();
          if (data.initialized) {
            // For existing users, ensure HideGettingStarted exists and defaults to false
            if (data.HideGettingStarted === undefined) {
              await updateDoc(userDocRef, { HideGettingStarted: false });
            }
            setHideGettingStarted(data.HideGettingStarted || false);
            setGettingStartedLoaded(true);
          } else {
            setShowWelcomeDialog(true);
          }
        } else {
          setShowWelcomeDialog(true);
        }
      };
      checkInitialization();
    }
  }, [user]);

  // Load items other users have shared with this user
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const loadShared = async () => {
      try {
        const shares = await fetchIncomingShares(user.uid);
        const { vehicles: sv, events: se } = await fetchSharedItems(shares);
        if (cancelled) return;
        setSharedVehicles(sv.map(v => ({ ...v, image: v.image || '/images/default-vehicle.jpg' })));
        setSharedEvents(se.map(e => ({ ...e, image: e.image || '/images/default-event.jpg' })));
      } catch (err) {
        console.error('Error loading shared items:', err.message);
      } finally {
        if (!cancelled) setSharedLoaded(true);
      }
    };
    loadShared();
    return () => { cancelled = true; };
  }, [user]);

  // Once all data is loaded, preload every image, then reveal the page at once.
  useEffect(() => {
    if (pageReady) return;
    if (!vehiclesLoaded || !eventsLoaded || !sharedLoaded || !gettingStartedLoaded) return;
    let cancelled = false;
    const toolImages = [
      '/images/tire-manager.jpg',
      '/images/circuit-manager.jpg',
      '/images/calculators.jpg',
      '/images/excuse-generator.jpg',
    ];
    const imageUrls = [
      ...vehicles.map(v => v.image),
      ...events.map(e => e.image),
      ...sharedVehicles.map(v => v.image),
      ...sharedEvents.map(e => e.image),
      ...toolImages,
    ];
    preloadImagesAsync(imageUrls).then(() => {
      if (!cancelled) setPageReady(true);
    });
    return () => { cancelled = true; };
  }, [vehiclesLoaded, eventsLoaded, sharedLoaded, gettingStartedLoaded, pageReady, vehicles, events, sharedVehicles, sharedEvents]);

  const allVehicles = [...vehicles, ...sharedVehicles];
  const allEvents = [...events, ...sharedEvents];

  const getVehicleKey = (vehicleId, ownerUid) => `${ownerUid || 'me'}::${vehicleId}`;
  const allSelected = selectedVehicleKeys.size === 0;
  const toggleVehicleKey = (key) => {
    setSelectedVehicleKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); // deselecting; empty falls back to "all"
      else next.add(key);
      return next;
    });
  };
  const eventVehicleIdList = (e) => e.vehicleIds || (e.vehicleId !== undefined && e.vehicleId !== null && e.vehicleId !== '' ? [e.vehicleId] : []);
  const modalEvents = allEvents
    .filter(e => allSelected || eventVehicleIdList(e).some(vid => selectedVehicleKeys.has(getVehicleKey(vid, e.ownerUid))))
    .slice()
    .sort((a, b) => {
      const da = new Date(a.startDate).getTime();
      const dbb = new Date(b.startDate).getTime();
      if (isNaN(da) && isNaN(dbb)) return 0;
      if (isNaN(da)) return 1;
      if (isNaN(dbb)) return -1;
      return dbb - da; // most recent first
    });

  const handleWelcomeDialogClose = async () => {
    setShowWelcomeDialog(false);
    // Initialize user with example data
    const userDocRef = doc(db, `users/${user.uid}`);

    // Helper functions
    const convertToGroups = (params) => {
      if (Array.isArray(params)) {
        return [{ title: 'Default', params }];
      }
      return Object.entries(params).map(([title, params]) => ({ title, params }));
    };

    const populateDefaults = (groups) => {
      groups.forEach(group => {
        group.params.forEach(param => {
          if (param.type === 'select' && param.options) {
            param.value = param.value || param.options[0];
          } else if (param.min !== undefined && param.max !== undefined) {
            const min = parseFloat(param.min);
            const max = parseFloat(param.max);
            param.value = ((min + max) / 2).toString();
          } else {
            param.value = param.value || '';
          }
        });
      });
      return groups;
    };



    // Create example vehicle with Club Track Day Metric template
    const vehicleId = Date.now().toString();
    const suspensionGroups = populateDefaults(convertToGroups(CarTemplates['Club Track Day'].Metric.Suspension));
    const tiresGroups = populateDefaults(convertToGroups(CarTemplates['Club Track Day'].Metric.Tires));
    const brakesGroups = populateDefaults(convertToGroups(CarTemplates['Club Track Day'].Metric.Brakes));
    const aeroGroups = populateDefaults(convertToGroups(CarTemplates['Club Track Day'].Metric.Aero));
    const drivetrainGroups = populateDefaults(convertToGroups(CarTemplates['Club Track Day'].Metric.Drivetrain));
    const engineGroups = populateDefaults(convertToGroups(CarTemplates['Club Track Day'].Metric.Engine));
    const electronicsGroups = populateDefaults(convertToGroups(CarTemplates['Club Track Day'].Metric.Electronics));

    const tabs = [
      { name: 'Info', groups: [{ title: 'General', params: [{ name: 'Vehicle Name', type: 'text', value: 'Example Vehicle' }, { name: 'Image', type: 'upload', value: '' }, { name: 'Vehicle Notes', type: 'textarea', value: '' }] }] },
      { name: 'Suspension', groups: suspensionGroups },
      { name: 'Tires', groups: tiresGroups },
      { name: 'Brakes', groups: brakesGroups },
      { name: 'Aero', groups: aeroGroups },
      { name: 'Drivetrain', groups: drivetrainGroups },
      { name: 'Engine', groups: engineGroups },
      { name: 'Electronics', groups: electronicsGroups },
    ];

    const vehicleData = {
      name: 'Example Vehicle',
      tabs: tabs,
      timestamp: Date.now()
    };
    await setDoc(doc(db, `users/${user.uid}/vehicles/${vehicleId}`), vehicleData);

    // Create example event
    const eventId = (Date.now() + 1).toString();
    const today = new Date().toISOString().split('T')[0];
    const eventData = {
      name: 'Example Event',
      vehicleId: vehicleId,
      startDate: today,
      endDate: today,
      circuit: 'Example Circuit',
      image: '',
      notes: '',
      timestamp: Date.now()
    };
    await setDoc(doc(db, `users/${user.uid}/events/${eventId}`), eventData);

    // Create example session with populated setup
    const sessionId = (Date.now() + 2).toString();
    const setup = {
      "ARB Setting": "50",
      "ARB Setting (Front)": "3.00",
      "ARB Setting (Rear)": "2.00",
      "Brake Balance": "66.7",
      "Camber": "0",
      "Camber (Front Left)": "3.2",
      "Camber (Front Right)": "3.2",
      "Camber (Rear Left)": "2.8",
      "Camber (Rear Right)": "2.8",
      "Circumference": "2000",
      "Circumference (Front)": "2000.00",
      "Circumference (Rear)": "2040.00",
      "Cold Tire Pressure": 2.1,
      "Cold Tire Pressure (Front Left)": 140,
      "Cold Tire Pressure (Front Right)": 140,
      "Cold Tire Pressure (Rear Left)": 140,
      "Cold Tire Pressure (Rear Right)": 140,
      "Corner Weights": "340",
      "Corner Weights (Front Left)": "500.0",
      "Corner Weights (Front Right)": "500.0",
      "Corner Weights (Rear Left)": "500.0",
      "Corner Weights (Rear Right)": "500.0",
      "Cross Weight": "50.0",
      "Drive Type": "RWD",
      "Drop Gear": "1.000",
      "Final Drive": "4.300",
      "Front Weight": "50",
      "Fuel Level": "20.0",
      "Gear Ratio 1": "3.700",
      "Gear Ratio 2": "2.300",
      "Gear Ratio 3": "1.700",
      "Gear Ratio 4": "1.300",
      "Gear Ratio 5": "1.150",
      "Gear Ratio 6": "1.000",
      "Gear Ratio 7": "1",
      "Gear Ratio 8": "1",
      "Hot Tire Pressure": 2.1,
      "Hot Tire Pressure (Front Left)": "",
      "Hot Tire Pressure (Front Right)": "",
      "Hot Tire Pressure (Rear Left)": "",
      "Hot Tire Pressure (Rear Right)": "",
      "Left Weight": "50",
      "Number of Gears": "6",
      "Power": 298.28000000000003,
      "Rev limit": "8000.00",
      "Ride Height": "500",
      "Ride Height (Front Left)": "114.0",
      "Ride Height (Front Right)": "114.0",
      "Ride Height (Rear Left)": "114.0",
      "Ride Height (Rear Right)": "114.0",
      "Spring Rate": "100",
      "Spring Rate (Front Left)": "140.00",
      "Spring Rate (Front Right)": "140.00",
      "Spring Rate (Rear Left)": "90.00",
      "Spring Rate (Rear Right)": "90.00",
      "Tire ID": "",
      "Tire Temperature": "80",
      "Toe": "0",
      "Toe (Front Left)": "-1.0",
      "Toe (Front Right)": "-1.0",
      "Toe (Rear Left)": "1.0",
      "Toe (Rear Right)": "1.0",
      "Total Weight": "2000.0",
      "Tread Depth": "10",
      "Tread Depth (Front Left)": "7.0",
      "Tread Depth (Front Right)": "7.0",
      "Tread Depth (Rear Left)": "7.0",
      "Tread Depth (Rear Right)": "7.0",
      "Wing Angle": "7.5"
    };
    const powerData = [
      { rpm: "1000.00", torque: "150.00" },
      { rpm: "2000.00", torque: "200.00" },
      { rpm: "3000.00", torque: "240.00" },
      { rpm: "4000.00", torque: "270.00" },
      { rpm: "5000.00", torque: "310.00" },
      { rpm: "6000.00", torque: "320.00" },
      { rpm: "7000.00", torque: "300.00" },
      { rpm: "8000.00", torque: "240.00" }
    ];
    const sessionData = {
      sessionName: 'Example Session',
      comment: 'Example Session',
      eventId: eventId,
      vehicleId: vehicleId,
      time: '2025-12-16T13:14',
      timestamp: Date.now(),
      tireIds: [],
      notes: {},
      balanceNotes: '',
      bestLap: '1:14.014',
      humidity: '14',
      lapTimes: '1:14.123,1:14.124,1:14.241,1:14.358,1:14.897,1:14.014,1:14.114,1:14.214,1:14.714,1:14.140,1:14.214,1:14.914,1:14.147,1:14.114',
      numLaps: '14',
      temperature: '14',
      trackNotes: [],
      trackTemp: '14',
      wind: '24',
      windDir: 'NNW',
      powerData: powerData,
      setup: setup
    };
    await setDoc(doc(db, `users/${user.uid}/sessions/${sessionId}`), sessionData);

    // Set initialized flag and default HideGettingStarted to false
    await setDoc(userDocRef, { initialized: true, HideGettingStarted: false }, { merge: true });
    setHideGettingStarted(false);
    setGettingStartedLoaded(true);
    await updateLastModified(user);
  };

  const addVehicle = () => {
    navigate('/vehicle-editor/new');
  };

  const addEvent = () => {
    if (vehicles.length === 0) {
      setShowEventWarning(true);
      return;
    }
    setShowEventEditor(true);
  };

  const selectVehicle = (v) => {
    setLocalSelectedVehicle(v.id);
    setSelectedVehicle(v.id);
    navigate(`/vehicle-editor/${v.id}`, { state: { ownerUid: v.ownerUid || null } });
  };

  const selectEvent = (e) => {
    setSelectedEvent(e.id);
    navigate(`/event/${e.id}`, { state: { vehicleId: e.vehicleId, ownerUid: e.ownerUid || null } });
  };

  const handleEventEditorClose = () => {
    setShowEventEditor(false);
  };

  // Show a skeleton of the page (greyed cards) with a spinner overlay until the
  // subscription status, all data, and all images are ready — then render in one shot.
  if (subscriptionStatus.loading || !pageReady) {
    const skeletonColumns = [
      { title: 'Events', count: 4 },
      { title: 'Vehicles', count: 4 },
      { title: 'Utilities', count: 4 },
    ];
    return (
      <div>
        <Header syncStatus={syncStatus} subscriptionStatus={subscriptionStatus} />
        <div className="main-container main-skeleton">
          {skeletonColumns.map(col => (
            <div className="main-section" key={col.title}>
              <h1 className="skeleton-heading">&nbsp;</h1>
              {Array.from({ length: col.count }).map((_, i) => (
                <div key={i} className="main-card skeleton-card"></div>
              ))}
            </div>
          ))}
        </div>
        <div className="skeleton-spinner-overlay">
          <div className="skeleton-spinner"></div>
          <p>Loading...</p>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header syncStatus={syncStatus} subscriptionStatus={subscriptionStatus} />
      <div className="main-container">
        <div className="main-section" style={!subscriptionStatus?.hasAccess ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
          <div className="page-title"><h1>Events {!subscriptionStatus?.hasAccess && <span style={{ fontSize: '14px', color: '#666' }}>(Pro Feature)</span>}</h1></div>
          {allEvents.length === 0 && <p>No events found.</p>}
          {allEvents.slice(0, 5).map(e => (
            <div
              key={`${e.ownerUid || 'me'}-${e.id}`}
              onClick={() => selectEvent(e)}
              className={`main-card ${e.shared ? 'main-card-shared' : ''}`}
              style={{
                backgroundImage: `url(${e.image})`,
                backgroundSize: 'cover',
              }}
            >
              {e.shared && <div className="shared-badge"><span className="material-icons">group</span>Shared</div>}
              <div className="main-card-overlay">
                <div className="event-name">{e.name || 'Unnamed Event'}</div>
                {getVehicleNames(e) && <div className="event-vehicle">{getVehicleNames(e)}</div>}
                <div className="event-date">{formatEventDate(e.startDate, e.endDate)}</div>
              </div>
            </div>
          ))}
          <div className="main-action-buttons">
            {allEvents.length > 5 && <button onClick={() => { setSelectedVehicleKeys(new Set()); setShowMoreEvents(true); }} className="btn-main-action">More</button>}
            <button onClick={addEvent} className="btn-main-action">Add Event</button>
          </div>
        </div>
        <div className="main-section" style={!subscriptionStatus?.hasAccess ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
          <div className="page-title"><h1>Vehicles {!subscriptionStatus?.hasAccess && <span style={{ fontSize: '14px', color: '#666' }}>(Pro Feature)</span>}</h1></div>
          {allVehicles.length === 0 && <p>No vehicles found.</p>}
          {allVehicles.slice(0, 5).map(v => (
            <div
              key={`${v.ownerUid || 'me'}-${v.id}`}
              onClick={() => selectVehicle(v)}
              className={`main-card ${v.shared ? 'main-card-shared' : ''} ${selectedVehicle === v.id ? 'main-card-selected' : ''}`}
              style={{
                backgroundImage: `url(${v.image})`,
                backgroundSize: 'cover',
              }}
            >
              {v.shared && <div className="shared-badge"><span className="material-icons">group</span>Shared</div>}
              <div className="main-card-overlay">
                <div className="event-name">{v.name || 'Unnamed Vehicle'}</div>
              </div>
            </div>
          ))}
          <div className="main-action-buttons">
            {allVehicles.length > 5 && <button onClick={() => setShowMoreVehicles(true)} className="btn-main-action">More</button>}
            <button onClick={handleAddVehicle} className={`btn-main-action ${subscriptionStatus?.plan === 'enthusiast' && vehicles.length >= 3 ? 'disabled' : ''}`}>
              Add Vehicle
              {subscriptionStatus?.plan === 'enthusiast' && vehicles.length >= 3 && <span className="pro-btn-overlay">PRO</span>}
            </button>
          </div>
        </div>
        <div className="main-section">
          {gettingStartedLoaded && !hideGettingStarted && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                <h1 style={{ margin: 0 }}>Getting Started Videos</h1>
                <button className="hide-button" style={{ position: 'static', margin: 0, marginTop: '-2px' }} onClick={(e) => { e.stopPropagation(); setShowHideDialog(true); }}>✕</button>
              </div>
              <div className="main-card getting-started-card">
                <div className="main-card-overlay getting-started-overlay">
                  <button className="nav-arrow left" onClick={(e) => { e.stopPropagation(); setCurrentVideoIndex((prev) => (prev - 1 + gettingStartedVideos.length) % gettingStartedVideos.length); }}><span className="material-icons" style={{ marginLeft: '10px' }}>arrow_back_ios</span></button>
                  <button className="nav-arrow right" onClick={(e) => { e.stopPropagation(); setCurrentVideoIndex((prev) => (prev + 1) % gettingStartedVideos.length); }}><span className="material-icons" style={{ marginLeft: '5px' }}>arrow_forward_ios</span></button>
                </div>
                {embedUrl && <iframe className="video-iframe" src={embedUrl} title="YouTube video player" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>}
              </div>
              <div className="video-dots">
                {gettingStartedVideos.map((_, index) => (
                  <span
                    key={index}
                    className={`dot ${index === currentVideoIndex ? 'active' : ''}`}
                    onClick={() => setCurrentVideoIndex(index)}
                  ></span>
                ))}
              </div>
            </>
          )}
          <div className="page-title"><h1>Utilities</h1></div>
          <div
            onClick={handleTireManagerClick}
            className="main-card"
            style={{
              backgroundImage: `url(/images/tire-manager.jpg)`,
              backgroundSize: 'cover',
              opacity: subscriptionStatus?.plan === 'enthusiast' ? 0.5 : 1,
              cursor: subscriptionStatus?.plan === 'enthusiast' ? 'not-allowed' : 'pointer'
            }}
          >
            <div className="main-card-overlay">
              <div className="event-name">Tire Manager</div>
            </div>
            {subscriptionStatus?.plan === 'enthusiast' && <div className="pro-overlay">PRO</div>}
          </div>
          <div
            onClick={() => subscriptionStatus?.hasAccess && navigate('/circuit-manager')}
            className="main-card"
            style={{
              backgroundImage: `url(/images/circuit-manager.jpg)`,
              backgroundSize: 'cover',
              opacity: !subscriptionStatus?.hasAccess ? 0.5 : 1,
              cursor: !subscriptionStatus?.hasAccess ? 'not-allowed' : 'pointer'
            }}
          >
            <div className="main-card-overlay">
              <div className="event-name">Circuit Manager {!subscriptionStatus?.hasAccess && '(Pro)'}</div>
            </div>
          </div>
          <div
            onClick={() => navigate('/tools')}
            className="main-card"
            style={{
              backgroundImage: `url(/images/calculators.jpg)`,
              backgroundSize: 'cover',
            }}
          >
            <div className="main-card-overlay">
              <div className="event-name">Tools</div>
            </div>
          </div>
          <div
            onClick={() => navigate('/excuses')}
            className="main-card"
            style={{
              backgroundImage: `url(/images/excuse-generator.jpg)`,
              backgroundSize: 'cover',
            }}
          >
            <div className="main-card-overlay">
              <div className="event-name">Excuse Generator</div>
            </div>
          </div>
        </div>
      </div>
      {showMoreEvents && (
        <div className="more-overlay" onClick={() => setShowMoreEvents(false)}>
          <div className="more-modal" onClick={(e) => e.stopPropagation()}>
            <div className="page-title">
              <h2>All Events</h2>
              <button className="close-button" onClick={() => setShowMoreEvents(false)} aria-label="Close">✕</button>
            </div>
            {allVehicles.length > 1 && (
              <div className="more-filter-pills">
                <button
                  className={`filter-pill ${allSelected ? 'filter-pill-active' : ''}`}
                  onClick={() => setSelectedVehicleKeys(new Set())}
                >
                  All Vehicles
                </button>
                {allVehicles.map(v => {
                  const key = getVehicleKey(v.id, v.ownerUid);
                  const active = !allSelected && selectedVehicleKeys.has(key);
                  return (
                    <button
                      key={key}
                      className={`filter-pill ${active ? 'filter-pill-active' : ''}`}
                      onClick={() => toggleVehicleKey(key)}
                    >
                      {v.name || 'Unnamed Vehicle'}
                    </button>
                  );
                })}
              </div>
            )}
            <div className="more-events-list">
              {modalEvents.length === 0 && <p className="more-empty">No events found.</p>}
              {modalEvents.map(e => (
                <div
                  key={`${e.ownerUid || 'me'}-${e.id}`}
                  onClick={() => { selectEvent(e); setShowMoreEvents(false); }}
                  className={`event-list-item ${e.shared ? 'event-list-item-shared' : ''}`}
                >
                  <div
                    className="event-list-thumb"
                    style={{ backgroundImage: `url(${e.image})` }}
                  />
                  <div className="event-list-info">
                    <div className="event-list-name">{e.name || 'Unnamed Event'}</div>
                    {getVehicleNames(e) && <div className="event-list-vehicle">{getVehicleNames(e)}</div>}
                    <div className="event-list-date">{formatEventDate(e.startDate, e.endDate)}</div>
                  </div>
                  {e.shared && (
                    <div className="event-list-shared"><span className="material-icons">group</span>Shared</div>
                  )}
                  <span className="material-icons event-list-chevron">chevron_right</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {showMoreVehicles && (
        <div className="more-overlay" onClick={() => setShowMoreVehicles(false)}>
          <div className="more-modal" onClick={(e) => e.stopPropagation()}>
            <div className="page-title">
              <h2>All Vehicles</h2>
              <button className="close-button" onClick={() => setShowMoreVehicles(false)} aria-label="Close">✕</button>
            </div>
            <div className="more-events-list">
              {allVehicles.length === 0 && <p className="more-empty">No vehicles found.</p>}
              {allVehicles.map(v => (
                <div
                  key={`${v.ownerUid || 'me'}-${v.id}`}
                  onClick={() => { selectVehicle(v); setShowMoreVehicles(false); }}
                  className={`event-list-item ${v.shared ? 'event-list-item-shared' : ''}`}
                >
                  <div
                    className="event-list-thumb"
                    style={{ backgroundImage: `url(${v.image})` }}
                  />
                  <div className="event-list-info">
                    <div className="event-list-name">{v.name || 'Unnamed Vehicle'}</div>
                  </div>
                  {v.shared && (
                    <div className="event-list-shared"><span className="material-icons">group</span>Shared</div>
                  )}
                  <span className="material-icons event-list-chevron">chevron_right</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {showEventWarning && (
        <div className="main-warning">
          <p>Add a vehicle first.</p>
          <button onClick={() => setShowEventWarning(false)} className="btn btn-secondary">OK</button>
        </div>
      )}
      {showEventEditor && (
        <EventEditor
          eventId="new"
          vehicleId={selectedVehicle}
          onClose={handleEventEditorClose}
          vehicles={vehicles}
        />
      )}
      {showVehicleLimitDialog && (
        <ConfirmationDialog
          title="Vehicle Limit Reached"
          message="Maximum of 3 vehicles in the Enthusiast Plan"
          onConfirm={handleUpgrade}
          onCancel={() => setShowVehicleLimitDialog(false)}
          confirmText="Upgrade to Pro"
          cancelText="Ok"
        />
      )}
      {showTireManagerDialog && (
        <ConfirmationDialog
          title="Pro Feature"
          message="Tire Manager is available with the Pro plan"
          onConfirm={handleUpgrade}
          onCancel={() => setShowTireManagerDialog(false)}
          confirmText="Upgrade to Pro"
          cancelText="Ok"
        />
      )}
      {showWelcomeDialog && <WelcomeDialog onClose={handleWelcomeDialogClose} navigate={navigate} />}
      {showHideDialog && (
        <ConfirmationDialog
          message="You can reactivate the Getting Started videos in the settings menu."
          onConfirm={handleHideGettingStarted}
          onCancel={() => setShowHideDialog(false)}
          confirmText="Hide"
          cancelText="Cancel"
        />
      )}
    </div>
  );
}

export default Main;